/**
 * Address normalization helpers for bill grouping.
 *
 * Two bills that point at the same physical location can arrive with
 * slightly different street1 values — one including a unit/floor/suite
 * designation ("7521 Jefferson AVE *2fl") and another without
 * ("7521 Jefferson AVE"). The server-side matcher (MatchingService
 * .matchServiceAddress) compares the formatted address as an EXACT
 * uppercased string, so it wouldn't collapse those either. More to the
 * point, the frontend groups bills into service-address buckets BEFORE the
 * match request is built: if grouping splits them, the server never gets a
 * chance to merge them and the user sees two service addresses for one site.
 *
 * Scope: this file only produces the grouping KEY. Callers (see
 * `buildMatchRequest` in `bill-ai-flow.store.ts`) use the key to bucket
 * bills, and the first bill in each bucket wins for display — we don't
 * stitch fields across bills, because different bills often put unit info
 * in different slots and merging produces redundant displays like
 * "…*2fl , 2fl".
 */

export interface StripUnitResult {
  cleanStreet: string;
  extractedUnit: string | null;
}

// Order matters: more-specific patterns run before the generic `#N` catch-all.
//
// The labelled-unit patterns are split deliberately. If we just wrote
// `(?:suite|ste|unit|...)\s+\S+`, we'd also eat real street names that
// happen to start with a unit-label word — "123 Ste Genevieve Rd" (a real
// St. Louis street) would strip to "123". The split requires either a
// digit in the value, OR a bare single letter that ends the street1
// string. "Ste Genevieve" is mid-string with no digit, so it survives;
// "... Unit B" at the end still gets captured.
const UNIT_LABEL = '(?:apt|apartment|suite|ste|unit|rm|room|bldg|building)';
const UNIT_PATTERNS: RegExp[] = [
  // Asterisk-prefixed designators like "*2fl", "*3 A"
  /\*\s*\d+\s*[a-z]*/gi,
  // Ordinal-floor forms: "2nd Floor", "3rd Fl", "1st Flr"
  /\b\d+(?:st|nd|rd|th)\s+(?:floors?|flrs?|fls?)\b\.?/gi,
  // Labelled units where the value contains a digit:
  // "Apt 5", "Apartment 3B", "Suite 200", "Ste. 100", "Unit #3", "Bldg A-12"
  new RegExp(`\\b${UNIT_LABEL}\\.?\\s*#?\\s*[a-z0-9-]*\\d[a-z0-9-]*`, 'gi'),
  // Labelled units with a bare single letter at end-of-string: "... Unit B"
  new RegExp(`\\b${UNIT_LABEL}\\.?\\s*#?\\s*[a-z](?=\\s*$)`, 'gi'),
  // Labelled floor forms (always digit-anchored): "Floor 2", "Fl 2", "Flr 2A"
  /\b(?:floors?|flrs?|fls?)\.?\s*#?\s*\d+[a-z]?\b/gi,
  // Bare number/letter after hash: "#3", "#3A", "#B"
  /#\s*[a-z0-9-]+/gi
];

/**
 * Splits a street1 value into its base street portion and any embedded
 * unit/floor/suite designation. Returns `{ cleanStreet, extractedUnit }`.
 *
 * Preserves original casing in the cleaned street — the caller decides
 * whether to lowercase it for key comparison or leave it intact for display.
 */
export function stripUnitFromStreet(street1: string | null | undefined): StripUnitResult {
  if (!street1) {
    return { cleanStreet: '', extractedUnit: null };
  }

  const extracted: string[] = [];
  let cleaned = street1;

  for (const pattern of UNIT_PATTERNS) {
    cleaned = cleaned.replace(pattern, (match) => {
      extracted.push(match.trim());
      return ' ';
    });
  }

  cleaned = cleaned.replace(/\s+/g, ' ').replace(/[\s,]+$/, '').trim();

  return {
    cleanStreet: cleaned,
    extractedUnit: extracted.length > 0 ? extracted.join(' ').trim() : null
  };
}

/**
 * Builds a normalized grouping key for a service address. The key is
 * case-insensitive, strips `.`, `,`, `#`, and `*`, collapses ASCII
 * whitespace, and ignores unit/floor/suite designators embedded in street1.
 *
 * Known limitations (callers should be aware):
 *  - No directional-prefix normalization ("123 N Main" ≠ "123 Main").
 *  - No street-type normalization ("Street" ≠ "St"); see
 *    `bill-grouping.util.ts::normalizeStreet` for the richer version used
 *    by the hierarchy grouper. Matching server-side via utility-account
 *    number is the authoritative dedup path for existing data.
 *
 * The returned string is suitable only as a Map key — never surface it
 * to the user. Display uses the original street1/street2 values.
 */
export function buildAddressGroupingKey(
  street1: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined
): string {
  const { cleanStreet } = stripUnitFromStreet((street1 || '').trim());
  const normalize = (s: string): string =>
    s.toLowerCase().replace(/[.,#*]/g, '').replace(/\s+/g, ' ').trim();

  return [
    normalize(cleanStreet),
    normalize(city || ''),
    normalize(state || '')
  ].join('|');
}
